import React, { useCallback, useState, useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import { PATH_URL, PATH_API } from 'src/helpers'
import { validate } from 'src/helpers/validation'
import { postHttp } from 'src/api/apiLayer'
import { getQueryParameters } from 'src/helpers/tool'
import { preserveUrlParams } from 'src/helpers/preserveUrlParams'
import { useAppContext } from 'src/helpers/app'
import { EMAIL_VALIDATION_MESSAGES } from './constants'
import styles from './styles.module.scss'

export const useEmailEntry = () => {
  const [email, setEmail] = useState('')
  const [isValidFormat, setIsValidFormat] = useState(true)
  const [errorMsg, setErrorMsg] = useState<React.ReactNode>(null)
  const [successMsg, setSuccessMsg] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [isEmailValid, setIsEmailValid] = useState(false)
  const [showError, setShowError] = useState(false)
  const history = useHistory()
  const { sessionID, storeURLParameters } = useAppContext()

  // Check for collectionId parameter and save sessionID if present
  useEffect(() => {
    const params = getQueryParameters()
    const collectionId = params.get('collectionId')

    if (collectionId && sessionID) {
      console.log('Collection ID detected in EmailEntryPage, saving sessionID to collectionsQuizEntry')
      const collectionsQuizEntry = JSON.parse(localStorage.getItem('collectionsQuizEntry') || '{}')
      collectionsQuizEntry.sessionID = sessionID
      collectionsQuizEntry.collectionId = collectionId
      localStorage.setItem('collectionsQuizEntry', JSON.stringify(collectionsQuizEntry))
    }
  }, [sessionID])

  const validateEmail = (email: string) => {
    return validate.email(email)
  }

  const checkEmailDuplicate = useCallback(
    async (emailToCheck: string) => {
      if (!emailToCheck.trim()) {
        setIsValidFormat(false)
        setErrorMsg(EMAIL_VALIDATION_MESSAGES.REQUIRED)
        setSuccessMsg(null)
        setIsEmailValid(false)
        return false
      }
      if (!validateEmail(emailToCheck)) {
        setIsValidFormat(false)
        setErrorMsg(EMAIL_VALIDATION_MESSAGES.INVALID)
        setSuccessMsg(null)
        setIsEmailValid(false)
        return false
      }
      setIsValidFormat(true)
      setErrorMsg(null)
      setSuccessMsg(null)
      setLoading(true)
      try {
        const response = await postHttp(PATH_API.checkMail, {
          email: emailToCheck,
          urlParameters: storeURLParameters ?? undefined,
        })
        if (response?.data?.available === true) {
          setIsValidFormat(true)
          setErrorMsg(null)
          setSuccessMsg('Email is valid! Proceeding to next step...')
          setIsEmailValid(true)
          // Automatically proceed to next step
          localStorage.setItem('user_email', emailToCheck)
          const collectionsQuizEntry = JSON.parse(localStorage.getItem('collectionsQuizEntry') || '{}')
          collectionsQuizEntry.customerInfo = {
            ...(collectionsQuizEntry.customerInfo || {}),
            emailAddress: emailToCheck,
            firstName: collectionsQuizEntry.customerInfo?.firstName || collectionsQuizEntry.fullName || '',
            lastName: collectionsQuizEntry.customerInfo?.lastName || '',
            partnerName: collectionsQuizEntry.customerInfo?.partnerName || collectionsQuizEntry.partnerFullName || '',
          }
          localStorage.setItem('collectionsQuizEntry', JSON.stringify(collectionsQuizEntry))

          // Small delay to show success message before redirecting
          setTimeout(() => {
            // Navigate to next page preserving URL parameters
            const nextUrl = preserveUrlParams(PATH_URL.COLLECTIONS_QUIZ_RESULT_CALCULATION)
            history.push(nextUrl)
          }, 1000)

          return true
        } else if (response?.data?.available === false) {
          setIsValidFormat(false)
          setErrorMsg(
            <span>
              We've met before! Log into your proposal{' '}
              <a
                className={styles.customerPortalLink}
                href="https://customers.poppyflowers.com"
                target="_blank"
                rel="noopener noreferrer"
              >
                here
              </a>
              .
            </span>
          )
          setSuccessMsg(null)
          setIsEmailValid(false)
          return false
        } else {
          setIsValidFormat(false)
          setErrorMsg(EMAIL_VALIDATION_MESSAGES.ERROR)
          setSuccessMsg(null)
          setIsEmailValid(false)
          return false
        }
      } catch (err: unknown) {
        setIsValidFormat(false)
        setErrorMsg(EMAIL_VALIDATION_MESSAGES.ERROR)
        setSuccessMsg(null)
        setIsEmailValid(false)
        return false
      } finally {
        setLoading(false)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [history]
  )

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newEmail = e.target.value
    setEmail(newEmail)
    const isValid = validateEmail(newEmail)
    setIsValidFormat(isValid)
    setIsEmailValid(isValid)
    setErrorMsg(null)
    setSuccessMsg(null)
    setShowError(false)
  }

  const handleSubmitEmail = useCallback(async () => {
    if (loading) return

    if (!validateEmail(email)) {
      setIsValidFormat(false)
      setErrorMsg(EMAIL_VALIDATION_MESSAGES.INVALID)
      setSuccessMsg(null)
      setIsEmailValid(false)
      setShowError(true)
      return
    }

    const isValid = await checkEmailDuplicate(email)
    if (isValid) {
      localStorage.setItem('user_email', email)
      const collectionsQuizEntry = JSON.parse(localStorage.getItem('collectionsQuizEntry') || '{}')
      collectionsQuizEntry.customerInfo = {
        ...(collectionsQuizEntry.customerInfo || {}),
        emailAddress: email,
        firstName: collectionsQuizEntry.customerInfo?.firstName || collectionsQuizEntry.fullName || '',
        lastName: collectionsQuizEntry.customerInfo?.lastName || '',
        partnerName: collectionsQuizEntry.customerInfo?.partnerName || collectionsQuizEntry.partnerFullName || '',
      }
      localStorage.setItem('collectionsQuizEntry', JSON.stringify(collectionsQuizEntry))
      console.log('Updated collectionsQuizEntry after email submission:', collectionsQuizEntry)
    }
  }, [email, loading, checkEmailDuplicate])

  const handleContinue = useCallback(() => {
    if (isEmailValid) {
      // Get collectionId from localStorage or URL
      const collectionsQuizEntry = JSON.parse(localStorage.getItem('collectionsQuizEntry') || '{}')
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const collectionId = collectionsQuizEntry.collectionId

      // Navigate to next page preserving URL parameters
      const nextUrl = preserveUrlParams(PATH_URL.COLLECTIONS_QUIZ_RESULT_CALCULATION)
      history.push(nextUrl)
    }
  }, [isEmailValid, history])

  return {
    email,
    isValidFormat,
    errorMsg,
    successMsg,
    loading,
    isEmailValid,
    handleEmailChange,
    handleSubmitEmail,
    handleContinue,
    showError,
  }
}
